import { createContext, useContext, useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { useQuery } from '@apollo/client/react'

import {
  useAccount,
  useChannelsConnected,
} from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'

import type { BufferHomeTaskKey } from './FirstSteps/useBufferHomeChecklist'
import {
  BUFFER_HOME_TASK_KEY,
  BUFFER_HOME_TASK_STATUS,
  useBufferHomeChecklist,
} from './FirstSteps/useBufferHomeChecklist'

export type OnboardingStatus = 'notStarted' | 'inProgress' | 'completed'
type HomeUserState = 'onboarding' | 'default'
type HomePageContextValue = {
  userState: HomeUserState
  hasChannels: boolean
  onboardingStatus: OnboardingStatus | null
  isTeamContext: boolean
  /**
   * True once every first-steps card visible to this user is non-pending
   * (the user either completed or dismissed each one). Filters out
   * checklist entries that aren't rendered for the current context (e.g.
   * `inviteTeam` for solo users).
   */
  firstStepsCompleted: boolean
  /**
   * True while the underlying first-steps checklist has not returned data
   * yet — consumers should defer celebration/swap logic until this clears.
   */
  firstStepsLoading: boolean
}

const VISIBLE_TASK_KEYS_TEAM: ReadonlyArray<BufferHomeTaskKey> = [
  BUFFER_HOME_TASK_KEY.ConnectChannel,
  BUFFER_HOME_TASK_KEY.CreateFirstPost,
  BUFFER_HOME_TASK_KEY.InviteTeam,
]

const VISIBLE_TASK_KEYS_SOLO: ReadonlyArray<BufferHomeTaskKey> = [
  BUFFER_HOME_TASK_KEY.ConnectChannel,
  BUFFER_HOME_TASK_KEY.CreateFirstPost,
  BUFFER_HOME_TASK_KEY.TryBufferApi,
]

const ONBOARDING_DURATION_DAYS = 30
const MS_PER_DAY = 1000 * 60 * 60 * 24

// `describe-yourself` onboarding step options that mark a user as part of a
// team. Mirrors the set used in `useChannels` for sidebar team detection.
const TEAM_CONTEXT_SLUGS = new Set([
  'company-marketing-team',
  'marketing-agency',
])

const GetOnboardingStatus = graphql(`
  query GetOnboardingStatus {
    account {
      id
      currentOrganization {
        id
        onboarding {
          status
          responses {
            stepSlug
            selectedOptions
            skipped
          }
        }
      }
    }
  }
`)

const HomePageContext = createContext<HomePageContextValue | null>(null)

function useHomeUserState(): HomeUserState {
  const { account } = useAccount()
  const createdAt = account?.createdAt

  /** @todo get the real information */
  const didCompleteAllOnboardingTasks = false

  if (!createdAt || didCompleteAllOnboardingTasks) {
    return 'default'
  }

  const daysSinceSignup =
    (Date.now() - new Date(createdAt).getTime()) / MS_PER_DAY

  return daysSinceSignup < ONBOARDING_DURATION_DAYS ? 'onboarding' : 'default'
}

export function HomePageProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const userState = useHomeUserState()
  const hasChannels = useChannelsConnected().length > 0
  const { data } = useQuery(GetOnboardingStatus)
  const onboarding = data?.account?.currentOrganization?.onboarding
  const onboardingStatus = onboarding?.status ?? null

  const describeYourselfResponse = onboarding?.responses?.find(
    (response) =>
      response.stepSlug === 'describe-yourself' && !response.skipped,
  )
  const isTeamContext =
    describeYourselfResponse?.selectedOptions.some((option) =>
      TEAM_CONTEXT_SLUGS.has(option),
    ) ?? false

  const { tasks, loading: firstStepsLoading } = useBufferHomeChecklist()
  const visibleTaskKeys = isTeamContext
    ? VISIBLE_TASK_KEYS_TEAM
    : VISIBLE_TASK_KEYS_SOLO
  const visibleTasks = tasks.filter((task) =>
    visibleTaskKeys.includes(task.taskKey),
  )
  const firstStepsCompleted =
    visibleTasks.length > 0 &&
    visibleTasks.every(
      (task) => task.status !== BUFFER_HOME_TASK_STATUS.Pending,
    )
  const contextValue = useMemo(
    () => ({
      userState,
      hasChannels,
      onboardingStatus,
      isTeamContext,
      firstStepsCompleted,
      firstStepsLoading,
    }),
    [
      userState,
      hasChannels,
      onboardingStatus,
      isTeamContext,
      firstStepsCompleted,
      firstStepsLoading,
    ],
  )

  return (
    <HomePageContext.Provider value={contextValue}>
      {children}
    </HomePageContext.Provider>
  )
}

export function useHomePage(): HomePageContextValue {
  const context = useContext(HomePageContext)
  if (context === null) {
    throw new Error('useHomePage must be used within a HomePageProvider')
  }
  return context
}
